import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useProgress } from "@/context/ProgressContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { ArrowRight, ListChecks, Megaphone, HelpCircle, HeartPulse, ClipboardList } from "lucide-react";
import { HajjTodayCard } from "@/components/HajjTodayCard";
import { HomePrayerCard } from "@/components/HomePrayerCard";
import { ItineraryTodayCard } from "@/components/itinerary/ItineraryTodayCard";
import { DashboardHero } from "@/components/dashboard/DashboardHero";
import { PromoBanner } from "@/components/dashboard/PromoBanner";
import { FavoritePackages } from "@/components/dashboard/FavoritePackages";
import { QuickLinks } from "@/components/dashboard/QuickLinks";
import { InstallHomeCard } from "@/components/install/InstallHomeCard";
import { ApkUpdateBanner } from "@/components/install/ApkUpdateBanner";
import { ApkRedownloadCard } from "@/components/install/ApkRedownloadCard";
import { SupportHomeCard } from "@/components/support/SupportHomeCard";
import { AskManasik } from "@/components/ask/AskManasik";
import { isPlayStoreApp } from "@/lib/apkVersion";

const LEARN_LINKS = [
  { to: "/faq", testid: "link-faq", icon: HelpCircle, key: "faqTitle" },
  { to: "/health", testid: "link-health", icon: HeartPulse, key: "healthTitle" },
  { to: "/prep", testid: "link-prep", icon: ClipboardList, key: "prepTitle" },
];

// Phone order (order-N, empty wrappers collapse): hero → ask → progress → today cards → menus; the big
// Install/Support cards are desktop-only (QuickLinks already has compact rows for them).
// Desktop keeps the DOM order (md:order-none). Literal class strings so Tailwind can see them.
const SLOT = {
  1: "order-1 md:order-none empty:hidden", 2: "order-2 md:order-none empty:hidden", 3: "order-3 md:order-none empty:hidden",
  4: "order-4 md:order-none empty:hidden", 5: "order-5 md:order-none empty:hidden", 6: "order-6 md:order-none empty:hidden",
  7: "order-7 md:order-none empty:hidden", 8: "order-8 md:order-none empty:hidden", 9: "order-9 md:order-none empty:hidden",
  11: "order-11 md:order-none empty:hidden", 12: "order-12 md:order-none empty:hidden",
};

export default function Dashboard() {
  const { t } = useTranslation();
  const { tc } = useLang();
  const { user } = useAuth();
  const { progress } = useProgress();
  const [stages, setStages] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    api.get(`/content/stages?trip=${progress.trip_type || "umrah"}`).then((r) => setStages(r.data.stages));
    api.get("/content/checklist").then((r) => setChecklist(r.data.checklist));
    api.get("/announcements").then((r) => setAnnouncements(r.data.announcements)).catch(() => {});
    api.get("/packages").then((r) => setPackages(r.data.packages)).catch(() => {});
  }, [progress.trip_type]);

  const favs = progress.favorite_packages || [];
  const totalStages = stages.length || 1;
  const done = stages.filter((s) => progress.completed_stages.includes(s.id)).length;
  const allItems = checklist.flatMap((c) => c.items);
  const checkedItems = allItems.filter((i) => progress.checklist[i.id]).length;

  const slot = (n) => SLOT[n];
  return (
    <div className="flex flex-col gap-5 sm:gap-6">
      <div className={slot(1)}><PromoBanner tc={tc} promoPkgs={packages.filter((p) => p.promo)} /></div>
      <div className={slot(2)}><DashboardHero name={user?.name} /></div>
      <div className={slot(3)}><AskManasik /></div>
      <div className={slot(4)}><ApkUpdateBanner /></div>
      <div className={slot(4)}><ApkRedownloadCard /></div>
      <div className={`${slot(11)} hidden md:block`}><InstallHomeCard /></div>
      {!isPlayStoreApp() && <div className={`${slot(11)} hidden md:block`}><SupportHomeCard /></div>}

      {announcements.length > 0 && (
        <div className={`${slot(4)} space-y-2`} data-testid="dashboard-announcements">
          {announcements.map((a) => (
            <Card key={a.id} className="rounded-2xl border-primary/30 bg-accent/40 p-3 sm:p-4 flex gap-3" data-testid={`announcement-${a.id}`}>
              <Megaphone className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-sm">{tc(a.title)}</p>
                {tc(a.body) && <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">{tc(a.body)}</p>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Today's manasik / countdown (Hajj season) → personal itinerary → location, Qibla, prayer times & wellness */}
      <div className={slot(6)}><HajjTodayCard /></div>
      <div className={slot(6)}><ItineraryTodayCard /></div>
      <div className={slot(6)}><HomePrayerCard /></div>

      <Card className={`${slot(5)} rounded-3xl border-border/50 shadow-sm p-5 sm:p-6`} data-testid="progress-card">
        <div className="flex items-center justify-between mb-3">
          <p className="font-medium">{t("journeyProgress")}</p>
          <span className="text-sm text-muted-foreground">{done} {t("of")} {totalStages} {t("complete")}</span>
        </div>
        <Progress value={Math.round((done / totalStages) * 100)} className="h-2.5" />
        <Link to="/guide">
          <Button className="mt-5 h-11 rounded-full w-full sm:w-auto px-6" data-testid="start-guide-btn">{t("startGuide")} <ArrowRight className="ms-2 h-4 w-4" /></Button>
        </Link>
      </Card>

      <div className={`${slot(7)} grid grid-cols-3 gap-3`} data-testid="dashboard-learn-links">
        {LEARN_LINKS.map(({ to, testid, icon: Icon, key }) => (
          <Link key={to} to={to} data-testid={testid} className="group flex flex-col items-center gap-2 rounded-2xl border border-border/50 bg-card p-3 sm:p-4 text-center shadow-sm transition-colors hover:border-primary/40">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-transform group-hover:scale-105"><Icon className="h-5 w-5" /></span>
            <span className="text-xs font-medium leading-tight">{t(key)}</span>
          </Link>
        ))}
      </div>

      <div className={slot(12)}><FavoritePackages tc={tc} favPackages={packages.filter((p) => favs.includes(p.id))} /></div>
      <div className={slot(9)}><QuickLinks t={t} tc={tc} totalStages={totalStages} /></div>

      <div className={`${slot(12)} grid md:grid-cols-2 gap-4`}>
        <Link to="/profile">
          <Card className="rounded-3xl border-border/50 shadow-sm p-5 sm:p-6 h-full" data-testid="checklist-summary">
            <div className="flex items-center gap-2 mb-3"><ListChecks className="h-5 w-5 text-primary" /><p className="font-medium">{t("quickChecklist")}</p></div>
            <p className="text-3xl font-serif">{checkedItems}<span className="text-muted-foreground text-lg">/{allItems.length}</span></p>
            <Progress value={allItems.length ? (checkedItems / allItems.length) * 100 : 0} className="h-2 mt-3" />
          </Card>
        </Link>
      </div>
    </div>
  );
}
